import { graphql } from "gatsby"
import PropTypes from "prop-types"
import React, { useState } from "react"
import Button from "components/Button"
import Card from "components/Card"
import CardGroup from "components/CardGroup"
import HubspotFormEmbed from "components/HubspotFormEmbed"
import Img from "components/Img"
import Modal from "components/Modal"
import ZoomRedirectModal from "components/ZoomRedirectModal"
import validateZoomUrl from "lib/validateZoomUrl"
import * as css from "./SupportSection.module.css"
import Section from "components/Layout/Section"
import SectionHeader from "components/Layout/SectionHeader"
import SimpleHeader from "components/Layout/SimpleHeader"

function SupportModal({ hubspotForm, closeModal, trackSubmit }) {
  const [isDone, setIsDone] = useState(0)

  return (
    <Modal onClose={closeModal} size="tiny">
      <SectionHeader title={hubspotForm.formLabel} centered />
      {!isDone ? (
        <HubspotFormEmbed
          formId={hubspotForm.formId}
          trackSubmit={trackSubmit}
          onSubmit={() => setIsDone(true)}
        />
      ) : (
        <div style={{ textAlign: "center" }}>
          <Button type="button" primary onClick={closeModal}>
            Go back
          </Button>
        </div>
      )}
    </Modal>
  )
}

function SupportSection({
  label,
  heading,
  subHeading,
  description,
  contentItems,
  supportFormUrl,
  hubspotForm,
  backgroundImage,
}) {
  const [open, setOpen] = useState(false)
  const [redirectUrl, setRedirectUrl] = useState(null)

  const closeModal = () => {
    setOpen(false)
  }

  const openModal = (event) => {
    event.preventDefault()
    setOpen(true)
  }

  return (
    <Section className={css.root}>
      {backgroundImage && (
        <div className={css.floatingBackground}>
          <Img image={backgroundImage} title={backgroundImage.title} />
        </div>
      )}
      <SimpleHeader
        level="h1"
        label={label}
        title={heading}
        subtitle={subHeading}
        titleSize="xl"
        subtitleSize="lg"
        dash="horizontal"
        narrow="both"
        contrasted
      />
      <CardGroup itemsPerRow={2} stackable>
        {contentItems?.map((item) => {
          let props = {}
          if (validateZoomUrl(item.ctaLink)) {
            props = {
              onClick: () => setRedirectUrl(item.ctaLink),
            }
          } else {
            props = { href: item.ctaLink }
          }
          return (
            <Card key={item.id} className={css.card} {...props}>
              {item.logo && (
                <img
                  className={css.icon}
                  src={item.logo.file.url}
                  alt={item.heading}
                />
              )}
              <div className={css.cardContent}>
                <div className={css.cardHeader}>{item.heading}</div>
                <div className={css.cardMeta}>{item.description}</div>
              </div>
            </Card>
          )
        })}
      </CardGroup>
      <p className={css.bottomText}>{description}</p>
      <Button
        className={css.button}
        href={supportFormUrl || "#"}
        onClick={supportFormUrl ? null : openModal}
        color="white"
      >
        Submit a support ticket
      </Button>
      {open && (
        <SupportModal hubspotForm={hubspotForm} closeModal={closeModal} />
      )}
      {redirectUrl && (
        <ZoomRedirectModal
          url={redirectUrl}
          closeModal={() => setRedirectUrl(null)}
        />
      )}
    </Section>
  )
}

SupportSection.propTypes = {
  label: PropTypes.string,
  heading: PropTypes.string,
  subHeading: PropTypes.string,
  description: PropTypes.string,
  contentItems: PropTypes.arrayOf(
    PropTypes.shape({
      ctaLink: PropTypes.string,
      ctaText: PropTypes.string,
      heading: PropTypes.string,
      description: PropTypes.string,
      logo: PropTypes.shape({
        url: PropTypes.string,
      }),
    })
  ),
  supportFormUrl: PropTypes.string,
  hubspotForm: PropTypes.shape({
    formId: PropTypes.string.isRequired,
    formLabel: PropTypes.string,
    thankYouMessage: PropTypes.string,
  }),
  backgroundImage: PropTypes.shape({}),
}

export default SupportSection

export const query = graphql`
  fragment SupportSection on ContentfulSupportSection {
    id
    __typename
    label
    heading
    subHeading
    description
    contentItems {
      ctaLink
      ctaText
      heading
      description
      logo {
        file {
          url
        }
      }
    }
    supportFormUrl
    hubspotForm {
      formId
      formLabel
      thankYouMessage
    }
    backgroundImage {
      gatsbyImageData(quality: 100)
      title
    }
  }
`
